import React, { useMemo } from 'react';
import { Icon, IconsEnum } from 'components/Icons';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';

import { BannerInformation } from 'components/bannerInformation';
import type { SportTicketEsito } from 'features/sport/components/sportTicket/utils/calcoloCarrello/types';
import classNames from 'classnames';
import { scommessaRemoved } from 'features/sport/components/sportTicket/sportTicketSlice';
import { selectEsitoIsSospeso } from 'features/sport/components/sportTicket/sportTicketSelectors';
import styles from './SportTicketItemEsito.module.scss';
import { useQuotaVariation } from 'hooks';
import { useGetLabelByKey, useTranslation } from 'hooks/useLingUI';

export type SportTicketItemEsitoProps = {
  cancelBtn: boolean;
} & SportTicketEsito;

export const SportTicketItemEsito = ({
  id,
  quota,
  isActive,
  cancelBtn,
  descrizioneEsito,
  descrizioneEsitoTrKey,
  descrizioneTipoScommessaWithInfoAgg,
  descrizioneTipoScommessaWithInfoAggTrKey,
}: SportTicketItemEsitoProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const esitoIsSospeso = useTypedSelector((state) => selectEsitoIsSospeso(state, id));
  const labelRimuoviScommessaAccessibility = useGetLabelByKey('rimuovi-scommessa-carrello-accessibility');
  const [isArrowVisible, isIncreasing, isDecreasing] = useQuotaVariation({ quota });

  const { quotaValue, isIncreasingArrowVisible, isDecreasingArrowVisible } = useMemo(() => {
    const quotaDividedHundred = (quota ?? 0) * 0.01;

    return {
      quotaValue:
        isActive && !esitoIsSospeso
          ? quotaDividedHundred < 10
            ? quotaDividedHundred!.toFixed(2)
            : quotaDividedHundred
          : '-',
      isIncreasingArrowVisible: isArrowVisible && isIncreasing && isActive && !esitoIsSospeso,
      isDecreasingArrowVisible: isArrowVisible && isDecreasing && isActive && !esitoIsSospeso,
    };
  }, [esitoIsSospeso, isActive, isArrowVisible, isDecreasing, isIncreasing, quota]);

  return (
    <React.Fragment key={`ticket-esito-${id}`}>
      <li className={styles.scommessaItem}>
        <p className={classNames(styles.scommessa, { [styles.disabled]: esitoIsSospeso })} suppressHydrationWarning>
          {t(descrizioneTipoScommessaWithInfoAggTrKey, descrizioneTipoScommessaWithInfoAgg)}:
          <span className={styles.esito} suppressHydrationWarning>
            {t(descrizioneEsitoTrKey, descrizioneEsito)}
          </span>
        </p>
        <div className={styles.quota}>
          <span
            className={classNames(styles.quotaWrapper, {
              [styles.changed]: isIncreasingArrowVisible || isDecreasingArrowVisible,
              [styles.increasing]: isIncreasingArrowVisible,
              [styles.decreasing]: isDecreasingArrowVisible,
            })}
          >
            {quotaValue}
          </span>
          {cancelBtn && (
            <button
              type="button"
              className={classNames(styles.cancelBtn, {
                [styles.marginSmall]: isIncreasingArrowVisible || isDecreasingArrowVisible,
              })}
              onClick={() => dispatch(scommessaRemoved(id))}
              aria-label={labelRimuoviScommessaAccessibility}
            >
              <Icon
                iconId={IconsEnum.CANCEL_FULL}
                className={styles.cancelIcon}
                color={'var(--color-on-bg-secondary)'}
              />
            </button>
          )}
        </div>
      </li>
      {esitoIsSospeso && (
        <BannerInformation
          text={t('evento-chiuso-sospeso', 'closed')}
          type="error"
          icon={IconsEnum.EXCLAMATION_CIRCLE}
          visible={true}
          layoutType="onlylabel"
          className={styles.bannerInfo}
        />
      )}
    </React.Fragment>
  );
};
